/* Rouqe: product carousel tabs/arrows and the mobile add-to-cart bar. Everything else is Dawn. */
(() => {
  if (customElements.get('rq-rail')) return;

  class RqRail extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('.rq-rail__panel'));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const last = this.tabs.length - 1;
          const next = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1 }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      });

      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-rq-track]');
        const prev = panel.querySelector('[data-rq-prev]');
        const next = panel.querySelector('[data-rq-next]');
        if (!track) return;
        const update = () => {
          const max = track.scrollWidth - track.clientWidth;
          if (prev) prev.disabled = track.scrollLeft <= 2;
          if (next) next.disabled = track.scrollLeft >= max - 2;
        };
        const step = (direction) => {
          const item = track.querySelector('.rq-rail__item');
          const width = item ? item.getBoundingClientRect().width : track.clientWidth / 2;
          const perView = Math.max(1, Math.floor(track.clientWidth / width) - 1);
          track.scrollBy({ left: direction * width * perView, behavior: 'smooth' });
        };
        track.addEventListener('scroll', update, { passive: true });
        if (prev) prev.addEventListener('click', () => step(-1));
        if (next) next.addEventListener('click', () => step(1));
        panel.rqUpdate = update;
        update();
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        if (i === index && panel.rqUpdate) panel.rqUpdate();
      });
    }
  }

  customElements.define('rq-rail', RqRail);

  /* Mobile add-to-cart bar: appears once the real button scrolls away and presses it. */
  class RqStickyBuy extends HTMLElement {
    connectedCallback() {
      const id = this.dataset.section;
      this.getSubmit = () => document.getElementById(`ProductSubmitButton-${id}`);
      if (!this.getSubmit() || !('IntersectionObserver' in window)) return;

      this.button = this.querySelector('[data-rq-sticky-add]');
      this.variantLabel = this.querySelector('[data-rq-sticky-variant]');
      this.priceLabel = this.querySelector('[data-rq-sticky-price]');

      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
        this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
        this.button.tabIndex = scrolledPast ? 0 : -1;
      });
      this.observer.observe(this.getSubmit());

      this.button.addEventListener('click', () => {
        const submit = this.getSubmit();
        if (submit && !submit.disabled) submit.click();
        else if (submit) submit.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      this.sync = () => {
        const submit = this.getSubmit();
        if (submit) {
          const label = submit.querySelector('span');
          this.button.textContent = (label ? label.textContent : submit.textContent).trim();
          this.button.disabled = submit.disabled;
        }
        const price = document.querySelector(`#price-${id} .price__sale:not(.hidden) .price-item--sale, #price-${id} .price-item--regular`);
        if (price && this.priceLabel) this.priceLabel.textContent = price.textContent.trim();
        const picker = document.getElementById(`variant-selects-${id}`);
        if (picker && this.variantLabel) {
          const values = Array.from(picker.querySelectorAll('input:checked, select')).map((el) => el.value);
          if (values.length) this.variantLabel.textContent = values.join(' / ');
        }
      };
      document.addEventListener('change', (event) => {
        if (event.target.closest && event.target.closest('variant-selects')) setTimeout(this.sync, 400);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('rq-sticky-buy')) customElements.define('rq-sticky-buy', RqStickyBuy);

  /* ---------- Free shipping progress and add-ons (snippets/rq-ship-nudge.liquid) ---------- */

  // The minimum is set in the store currency. For another currency, convert it with Shopify's rate
  // and round up to a whole unit, so the bar never says "free" before checkout does.
  const rqShipState = (el) => {
    const { currency, shopCurrency } = el.dataset;
    // No free shipping for this shopper's country.
    if (el.dataset.eligible === 'false') return null;
    let threshold = Number(el.dataset.threshold) || 0;
    if (currency !== shopCurrency) {
      const active = window.Shopify && window.Shopify.currency;
      const rate = active && active.active === currency ? Number(active.rate) : NaN;
      if (!rate) return null;
      threshold = Math.ceil((threshold / 100) * rate) * 100;
    }
    const total = Number(el.dataset.total) || 0;
    return { threshold, total, gap: Math.max(0, threshold - total), currency };
  };

  const rqMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  class RqShipBar extends HTMLElement {
    connectedCallback() {
      // Rendered in full by Liquid unless the cart is in another currency.
      if (!this.hasAttribute('data-pending')) return;
      const state = rqShipState(this);
      if (!state) {
        this.hidden = true;
        return;
      }
      const msg = this.querySelector('.rq-ship__msg');
      if (state.gap > 0) {
        const [before, ...after] = (this.dataset.textAway || '').split('[amount]');
        const amount = document.createElement('strong');
        amount.textContent = rqMoney(state.gap, state.currency);
        msg.replaceChildren(before, amount, after.join('[amount]'));
      } else {
        msg.textContent = this.dataset.textDone || '';
      }
      const progress = Math.min(100, Math.floor((state.total * 100) / state.threshold));
      this.querySelector('.rq-ship__fill').style.setProperty('--rq-ship', `${progress}%`);
      this.classList.toggle('is-done', state.gap === 0);
      this.removeAttribute('data-pending');
    }
  }

  class RqCartAddons extends HTMLElement {
    connectedCallback() {
      this.arrange();
      // Cart page: the add-ons sit outside the part Dawn re-renders, so refresh them on cart changes.
      if (this.hasAttribute('data-refresh') && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    // "Complete the set" rows always stay. Free shipping add-ons: cheapest one that reaches the
    // minimum first, then the ones that come closest; hidden once shipping is free.
    arrange() {
      const state = rqShipState(this);
      const gap = state ? state.gap : 0;
      const price = (item) => Number(item.dataset.price) || 0;
      this.querySelectorAll('.rq-addon__hint').forEach((hint) => {
        hint.hidden = gap <= 0 || price(hint.closest('.rq-addon')) < gap;
      });

      const extra = this.querySelector('.rq-addons__group--extra');
      if (extra) {
        const items = Array.from(extra.querySelectorAll('.rq-addon'));
        if (gap <= 0 || items.length === 0) {
          extra.hidden = true;
        } else {
          const reach = items.filter((item) => price(item) >= gap).sort((a, b) => price(a) - price(b));
          const near = items.filter((item) => price(item) < gap).sort((a, b) => price(b) - price(a));
          const ordered = reach.concat(near);
          const limit = Number(this.dataset.count) || 3;
          // One of each product type first, then fill any free spots in price order.
          const types = new Set();
          const visible = new Set();
          ordered.forEach((item) => {
            if (visible.size < limit && !types.has(item.dataset.type)) {
              types.add(item.dataset.type);
              visible.add(item);
            }
          });
          ordered.forEach((item) => {
            if (visible.size < limit) visible.add(item);
          });
          const list = extra.querySelector('.rq-addons__list');
          ordered.forEach((item) => {
            item.hidden = !visible.has(item);
            list.appendChild(item);
          });
          extra.hidden = false;
        }
      }
      this.hidden = !this.querySelector('.rq-addons__group:not([hidden])');
    }

    async refresh() {
      const section = this.closest('[id^="shopify-section-"]');
      if (!section) return;
      try {
        const response = await fetch(`${window.location.pathname}?section_id=${section.id.replace('shopify-section-', '')}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('rq-cart-addons');
        if (fresh) this.replaceWith(fresh);
        else this.hidden = true;
      } catch (e) {
        /* keep what's there */
      }
    }
  }

  if (!customElements.get('rq-ship-bar')) customElements.define('rq-ship-bar', RqShipBar);
  if (!customElements.get('rq-cart-addons')) customElements.define('rq-cart-addons', RqCartAddons);

  // Add from the cart drawer without leaving it. On the cart page the form posts normally and
  // Shopify returns to the cart.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest && event.target.closest('.rq-addon__form');
    const drawer = form && form.closest('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const error = form.parentElement.querySelector('.rq-addon__error');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const body = new FormData(form);
    body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
    body.append('sections_url', window.location.pathname);
    try {
      const response = await fetch(window.routes ? window.routes.cart_add_url : '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);
      drawer.renderContents(data);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rq-addon', productVariantId: body.get('id'), cartData: data });
      }
    } catch (e) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (error) {
        error.textContent = e.message || 'Could not add this item.';
        error.hidden = false;
      }
    }
  });

  // Theme editor: selecting a tab block opens that tab.
  document.addEventListener('shopify:block:select', (event) => {
    const rail = event.target.closest && event.target.closest('rq-rail');
    if (rail && rail.tabs) {
      const index = rail.tabs.indexOf(event.target);
      if (index > -1) rail.select(index);
    }
  });
})();
